import { html, LitElement } from 'lit';
import { customElement, property, state } from 'lit/decorators.js';
import { styles } from '../view/styles';
import { translateFn } from '../../../utils/translator.helper';
import { INLINE_ACTIONS, IOC_TYPES } from '../types';
import { GzSelectWebComponent } from '../../../ui/gzSelect';
import { SelectEvents } from '../../../ui/gzSelect/types';
import { WebComponentEvent } from '../../../framework/BaseWebComponentController';
import { copyToClipboard } from '../../../utils/htmlElement.utils';
import _ from 'lodash';
import { pivotToCumulativeSearchIntelliZone, pivotToInProductTrialsIntelliZone } from '../../../utils/xdrOverview/intellizone/intellizone.utils';
import { IntelliZoneIntegrationData, IOC_POSSIBLE_TYPES_FOR_IZ } from '../../../utils/xdrOverview/intellizone/intellizone.types';
import { Rights } from '@nirvana/shared/constants';

/**
 * @tag groupAttributionIocSidePanel
 * Here https://custom-elements-manifest.open-wc.org/analyzer/getting-started/ you can find out how to document the code
 */
@customElement('gz-group-attribution-ioc-side-panel')
export class GroupAttributionIocSidePanelWebComponent extends LitElement {
  @property({
    type: Object,
    attribute: true,
    reflect: true,
  })
  _data;

  @property({
    type: Object,
    attribute: true,
    reflect: true,
  })
  intelliZoneIntegrationData: IntelliZoneIntegrationData;

  @property({
    type: String,
    attribute: true,
    reflect: true,
  })
  companyId = null;

  @state()
  private _currentData;

  // see https://lit-element.polymer-project.org/guide/styles
  static get styles() {
    return styles;
  }

  get data() {
    return this._data;
  }

  set data(prop) {
    const oldData = this._data;
    this._data = prop;
    this._currentData = _.cloneDeep(this._data);
    this.requestUpdate('data', oldData);
  }

  render() {
    return html`
      <div class="header pb-3 pr-2">
        <gz-tooltip class="mb-2" tooltipText="${translateFn('IoCs details')}" isEllipsis>
          <gz-title class="d-inline" fontsize="16" fontWeight="500">${translateFn('IoCs details')}</gz-title>
        </gz-tooltip>

        <gz-input-search
          placeholder="${translateFn('Search IoC')}"
          @change="${this.handleInputChange}"
        ></gz-input-search>
      </div>
      <div class="content">
        ${this.renderAccordion('MD5', IOC_TYPES.MD5)} ${this.renderAccordion('SHA256', IOC_TYPES.SHA256)}
        ${this.renderAccordion('URL', IOC_TYPES.URL)} ${this.renderAccordion('IP', IOC_TYPES.IP)}
        ${this.renderAccordion('DOMAIN', IOC_TYPES.DOMAIN)}
        ${this.renderAccordion('REGISTRY KEY', IOC_TYPES.REGISTRY_KEY)}
        ${this.renderAccordion('DROPPED FILE NAMES', IOC_TYPES.DROPPED_FILE_NAMES)}
        ${this.renderAccordion('PROCESS NAMES', IOC_TYPES.PROCESS_NAMES)}
      </div>
    `;
  }

  renderAccordion(title, type) {
    const accordionTitle = `${translateFn(title)} (${this.getNumberOfIocs(type)})`;
    return this.getNumberOfIocs(type)
           ? html`
        <gz-accordion title="${accordionTitle}" accordiontitle="${accordionTitle}">
          ${this.renderAccordionBody(type)}
        </gz-accordion>`
           : null;
  }

  handleInputChange(event) {
    const searchInput = event.detail.sourceEvent.trim().toLowerCase();
    this._currentData = this.data.filter(ioc => ioc.value.toLowerCase().includes(searchInput));
  }

  getNumberOfIocs(type) {
    const iocs = this._currentData.filter(ioc => ioc.type === type);
    return iocs.length;
  }

  renderAccordionBody(type: IOC_TYPES) {
    const iocs = this._currentData.filter(ioc => ioc.type === type);
    return iocs.map(
      ioc => html`
        <div class="container d-flex flex-column">
          <div class="d-flex flex-row justify-content-between">
            <gz-tooltip class="flex-1" tooltipText="${ioc.value}" isEllipsis>
              <gz-text class="d-inline" fontSize="12">${ioc.value}</gz-text>
            </gz-tooltip>
            ${this.renderInlineButton(ioc.type, ioc.value)}
          </div>
        </div>
      `,
    );
  }

  private getSelectOptions(iocType: IOC_TYPES) {
    const selectOptions = [
      {
        title: translateFn('Copy to clipboard'),
        value: INLINE_ACTIONS.COPY,
      },
      {
        title: translateFn('View events and alerts'),
        value: INLINE_ACTIONS.SEARCH,
      },
      {
        title: translateFn('View in Incidents'),
        value: INLINE_ACTIONS.INCIDENTS,
      }
    ];
    if (IOC_POSSIBLE_TYPES_FOR_IZ.includes(iocType)) {
      selectOptions.push(
        {
          title: translateFn('Search in IntelliZone'),
          value: INLINE_ACTIONS.INTELLIZONE,
          ...this.canSelectIntelliZone(),
        },
      );
    }
    return selectOptions;
  }

  private getSelectTemplate() {
    return `<gz-icon-button icon="ds-icon-more" buttoncolor="secondary" buttontype="square"
        fontsize="22" size="24" buttonfill="quiet" class="inline-menu-trigger"></gz-icon-button>`;
  }

  renderInlineButton(iocType: IOC_TYPES, iocValue: string) {
    const select = document.createElement('gz-select') as GzSelectWebComponent;
    select.options = this.getSelectOptions(iocType);
    select.innerHTML = this.getSelectTemplate();
    select.addEventListener(SelectEvents.SELECTION_CHANGED, async (event: WebComponentEvent) => {
      if (event.detail.selected === INLINE_ACTIONS.COPY) {
        copyToClipboard(iocValue);
      }
      if (event.detail.selected === INLINE_ACTIONS.INTELLIZONE) {
        this.handlePivotToIntelliZone(iocValue);
      }
      if (event.detail.selected === INLINE_ACTIONS.SEARCH) {
        this.handlePivotToSearch(iocType, iocValue);
      }
      if (event.detail.selected === INLINE_ACTIONS.INCIDENTS) {
        this.handlePivotToIncidents(iocType, iocValue);
      }
    });

    return select;
  }

  private handlePivotToIncidents(iocType: IOC_TYPES, iocValue: string) {
    Application.lib.Navigation.openInNewTab(`/incidents/filter/${iocType}/${this.companyId}&${encodeURIComponent(iocValue)}`);
  }

  private handlePivotToSearch(iocType: IOC_TYPES, iocValue: string) {
    const searchValue = encodeURIComponent(iocValue);
    Application.lib.Navigation.openInNewTab(`/xdr/events/${iocType}/${this.companyId}&${searchValue}`);
  }

  canSelectIntelliZone() {
    // Will be able to select, and it will be pivoted to In Product Trials
    if (this.intelliZoneIntegrationData.hasAccessToIZProductTrial && !this.intelliZoneIntegrationData.canPivotToIntelliZone) {
      return {
        disabled: false,
        tooltipText: undefined,
      };
    }

    // Will not be able to select, since it doesn't have access to In Product Trials
    if (!this.intelliZoneIntegrationData.canPivotToIntelliZone) {
      return {
        disabled: true,
        tooltipText: !Application.Main.hasAnyRights([Rights.RIGHT_COMPANY_MANAGER])
                     ? translateFn('Your company requires an integration with IntelliZone to access this feature. Please contact a user with Company Administrator rights.')
                     : translateFn('Your company does not have an active integration with IntelliZone. Contact your Account Manager to request access.'),
      };
    }

    // Can safely search in IntelliZone
    return {
      disabled: false,
      tooltipText: undefined,
    };
  }

  handlePivotToIntelliZone(iocValue) {
    if (this.intelliZoneIntegrationData.hasAccessToIZProductTrial && !this.intelliZoneIntegrationData.canPivotToIntelliZone) {
      pivotToInProductTrialsIntelliZone();
      return;
    }
    pivotToCumulativeSearchIntelliZone(this.intelliZoneIntegrationData.intellizoneUrl, {
      indicator: iocValue,
    });
  }

  constructor() {
    super();
  }
}

declare global {
  interface HTMLElementTagNameMap {
    groupAttributionIocSidePanel: GroupAttributionIocSidePanelWebComponent;
  }
}
